<template>
    <div>
        <ConfirmDialog
            :is-open="deleteRoomReservationDialog"
            :data="roomReservation"
            @on-confirm="removeRoomReservation(roomReservation)"
            @on-abort="closeDeleteRoomReservationDialog"
        >
            <template #title>
                Usuwanie rezerwacji
            </template>
            <template #content>
                Czy na pewno chcesz usunąć rezerwację?
            </template>
            <template #confirmButton>
                Usuń
            </template>
        </ConfirmDialog>

        <v-dialog
            :value="isOpen"
            max-width="500px"
            transition="slide-y-transition"
            eager
            @keydown.esc="onClose"
            @click:outside="onClose"
        >
            <v-card>
                <v-card-title>
                    <h1 class="text-subtitle-1">
                        <template v-if="reservationData.actionName === 'create'">
                            Tworzenie rezerwacji: <strong>{{ reservationDate }}</strong>
                        </template>
                        <template v-else>
                            Edycja rezerwacji: <strong>{{ reservationDate }}</strong>
                        </template>
                    </h1>
                </v-card-title>
                <v-card-text>
                    <v-row>
                        <v-col cols="12">
                            <v-text-field
                                v-model="roomReservation.name"
                                label="Nazwa rezerwacji"
                                :error-messages="roomNameErrors"
                                :disabled="reservationData.actionName === 'update'"
                                outlined
                                dense
                                required
                            />
                        </v-col>
                        <v-col v-if="reservationData.actionName === 'update'" cols="12">
                            <v-text-field
                                :value="userNameHandler(roomReservation.createdBy)"
                                label="Rezerwujący"
                                :disabled="reservationData.actionName === 'update'"
                                outlined
                                dense
                                required
                            />
                        </v-col>
                        <v-col cols="12">
                            <v-select
                                v-model="roomReservation.roomTypeId"
                                label="Salka"
                                :items="roomReservationsTypes"
                                item-value="id"
                                item-text="title"
                                no-data-text="Nie znaleziono typu salki"
                                :error-messages="roomTypeIdErrors"
                                :disabled="reservationData.actionName === 'update'"
                                outlined
                                dense
                                required
                            />
                        </v-col>
                        <v-col v-if="reservationData.actionName === 'update'" cols="12">
                            <v-select
                                v-model="roomReservation.roomReservationStatusId"
                                :items="roomReservationsStatuses"
                                item-value="id"
                                item-text="name"
                                no-data-text="Nie znaleziono statusu"
                                label="Status"
                                :disabled="!canConfirmReservation"
                                outlined
                                dense
                                required
                            />
                        </v-col>
                        <v-col v-if="reservationData.actionName !== 'update'" cols="12">
                            <v-slider
                                v-model="defaultTime"
                                label="Długość rezerwacji"
                                thumb-label="always"
                                hide-details
                                ticks
                                always-dirty
                                :disabled="roomReservation.allDay"
                                :min="15"
                                :max="maxTime"
                                :step="15"
                            />
                        </v-col>
                        <v-col v-if="reservationData.actionName !== 'update'" cols="12">
                            <v-checkbox
                                v-model="roomReservation.allDay"
                                label="Rezerwacja na cały dzień"
                            />
                        </v-col>
                        <v-col cols="12">
                            <v-autocomplete
                                v-model="roomReservation.invitedPerson"
                                label="Osoby zaproszone"
                                :items="users"
                                :filter="customUsersFilter"
                                :loading="usersSearchLoading"
                                :search-input.sync="usersSearch"
                                class="v-text-field--placeholder-dark"
                                required
                                dense
                                multiple
                                chips
                                outlined
                                deletable-chips
                                cache-items
                                item-value="id"
                                no-data-text="Nie znaleziono"
                                clearable
                                @change="clearUserSearch()"
                            >
                                <template slot="no-data">
                                    <v-list-item>
                                        <v-list-item-title>
                                            <template v-if="!usersSearch">
                                                Wpisz treść aby odnaleźć <strong>użytkownika</strong>.
                                            </template>
                                            <template v-else-if="usersSearchLoading">
                                                Trwa wyszukiwanie...
                                            </template>
                                            <template v-else>
                                                Nie znaleziono
                                            </template>
                                        </v-list-item-title>
                                    </v-list-item>
                                </template>
                                <template slot="selection" slot-scope="data">
                                    <template v-if="data.item.firstName && data.item.lastName">
                                        <v-chip
                                            close
                                            @update:active="removeUser(data.item)"
                                        >
                                            {{ data.item.firstName }} {{ data.item.lastName }}
                                        </v-chip>
                                    </template>
                                    <template v-else>
                                        <v-chip
                                            close
                                            @update:active="removeUser(data.item)"
                                        >
                                            {{ data.item.username }}
                                        </v-chip>
                                    </template>
                                </template>

                                <template slot="item" slot-scope="data">
                                    <template v-if="data.item.firstName && data.item.lastName">
                                        {{ data.item.firstName }} {{ data.item.lastName }}
                                    </template>
                                    <template v-else>
                                        {{ data.item.username }}
                                    </template>
                                </template>
                            </v-autocomplete>
                        </v-col>
                    </v-row>
                </v-card-text>

                <v-divider />

                <v-card-actions>
                    <v-btn
                        :color="reservationData.actionName === 'create' ? 'error' : 'info'"
                        text
                        data-sentry-label="Anuluj"
                        :disabled="actionLoading"
                        @click="onClose()"
                    >
                        Anuluj
                    </v-btn>
                    <v-spacer />
                    <v-btn
                        v-if="reservationData.actionName === 'update' && canDeleteRoomReservation"
                        color="error"
                        text
                        data-sentry-label="Usuń"
                        :loading="actionLoading"
                        @click="deleteRoomReservation()"
                    >
                        Usuń
                    </v-btn>
                    <v-btn
                        color="success"
                        text
                        data-sentry-label="Zapisz"
                        :loading="actionLoading"
                        @click="saveRoomReservation()"
                    >
                        Zapisz
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import ConfirmDialog from '@/components/common/ConfirmDialog'

import filters from '@/mixins/customFilters'
import customFilters from '@/mixins/customFilters'

import { hasUserPermission } from '@/utils/permissionsHandler'
import { errorHandler, addSentryBreadcrumb } from '@/utils/errorHandler'

import { required, minLength } from 'vuelidate/lib/validators'
import { format, addMinutes, setHours, setMinutes } from 'date-fns'
import { orderBy } from 'lodash'

let searchTimer

export default {
    name: 'RoomReservationDialog',
    components: {
        ConfirmDialog
    },
    mixins: [
        filters, customFilters
    ],
    props: {
        isOpen: {
            type: Boolean
        },
        reservationData: {
            type: Object,
            default: null
        },
    },
    data() {
        return {
            roomReservation: {
                name: null,
                roomTypeId: null,
                startDate: null,
                endDate: null,
                allDay: false,
                invitedPerson: []

            },
            pageTitle: '',
            maxTime: 480,
            defaultTime: 15,
            actionLoading: false,
            deleteRoomReservationDialog: false,
            reservationDate: null,
            usersSearch: null,
            usersSearchLoading: false,
            users: [],
        }
    },
    validations: {
        roomReservation: {
            name: {
                minLength: minLength(2),
                required
            },
            roomTypeId: {
                required
            },
        }
    },
    computed: {
        roomReservationsStatuses() {
            return this.$store.state.administration.roomReservations.roomReservationsStatuses
        },
        roomReservationsTypes() {
            return orderBy(this.$store.state.administration.roomReservations.roomReservationsTypes, 'codeName', 'asc')
        },
        canEditRoomReservation() {
            return hasUserPermission('room_reservations_update')
        },
        canDeleteRoomReservation() {
            return hasUserPermission('room_reservations_delete')
        },
        canConfirmReservation() {
            return hasUserPermission('room_reservations_admin_update')
        },
        // Validations
        roomNameErrors() {
            const errors = []

            if (!this.$v.roomReservation.name.$dirty) return errors
            if (!this.$v.roomReservation.name.minLength)
                errors.push('Nazwa rezerwacji musi zawierać minimum 2 znaki.')
            if (!this.$v.roomReservation.name.required)
                errors.push('Nazwa rezerwacji jest wymagana.')

            return errors
        },
        roomTypeIdErrors() {
            const errors = []

            if (!this.$v.roomReservation.roomTypeId.$dirty) return errors
            if (!this.$v.roomReservation.roomTypeId.required)
                errors.push('Salka jest wymagana.')

            return errors
        },
    },
    watch: {
        reservationData: {
            handler: function(newValue) {
                if (newValue.info === null) return

                if (newValue.actionName === 'create') {
                    this.roomReservation.startDate = newValue.info.dateStr
                    this.reservationDate = `${format(new Date(newValue.info.dateStr), 'YYYY-MM-DD')},  ${format(new Date(newValue.info.dateStr), 'HH:mm')}`
                    this.roomReservation.roomTypeId = parseInt(newValue.info.resource.id)

                    return
                } else {
                    this.roomReservation = newValue.reservationData
                    this.users = newValue.reservationData.invitedPersonRead
                    this.reservationDate = `${format(new Date(newValue.info.event.extendedProps.startDate), 'YYYY-MM-DD')}, ${format(new Date(newValue.info.event.start), 'HH:mm')} - ${format(new Date(newValue.info.event.end), 'HH:mm')}`
                }
            },
        },
        'roomReservation.allDay': function() {
            if (this.roomReservation.allDay) {
                this.roomReservation.startDate = setHours(new Date(this.roomReservation.startDate), 8)
                this.roomReservation.startDate = setMinutes(new Date(this.roomReservation.startDate), 0)
                this.roomReservation.endDate = setHours(new Date(this.roomReservation.startDate), 16)
                this.roomReservation.endDate = setMinutes(new Date(this.roomReservation.endDate), 0)
            }
        },
        usersSearch(value = null) {
            if (value === null) return

            this.usersSearchLoading = true

            clearTimeout(searchTimer)

            searchTimer = setTimeout(() => {
                if (value) {
                    this.searchUsers(value)
                } else {
                    this.usersSearchLoading = false
                    this.users = []
                }
            }, 500)
        }
    },
    methods: {
        clearUserSearch() {
            return this.usersSearch = null
        },
        removeUser(user) {
            this.roomReservation.invitedPerson.splice(this.roomReservation.invitedPerson.indexOf(user.id), 1)
        },
        saveRoomReservation() {
            this.$v.$touch()
            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })

                return
            }
            if (this.reservationData.actionName === 'create') {
                this.actionLoading = true
                if (!this.roomReservation.allDay) this.roomReservation.endDate = addMinutes(new Date(this.roomReservation.startDate), this.defaultTime)
                this.$store.dispatch('administration/saveRoomReservation', this.roomReservation )
                    .catch(error => {
                        errorHandler(error)
                    })
                    .finally(() => {
                        this.onClose()
                    })
            }
            if (this.reservationData.actionName === 'update') {
                this.actionLoading = true
                this.$store.dispatch('administration/updateRoomReservation', this.roomReservation )
                    .catch(error => {
                        errorHandler(error)
                    })
                    .finally(() => {
                        this.onClose()
                    })
            }
        },
        deleteRoomReservation() {
            this.deleteRoomReservationDialog = true
        },
        closeDeleteRoomReservationDialog() {
            this.deleteRoomReservationDialog = false
        },
        removeRoomReservation() {
            this.actionLoading = true
            this.$store.dispatch('administration/deleteRoomReservation', this.roomReservation )
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.closeDeleteRoomReservationDialog()
                    this.onClose()
                })
        },
        onClose() {
            this.actionLoading = false
            this.$emit('on-close')
            this.$v.$reset()
            setTimeout(() => {
                this.roomReservation = {},
                this.defaultTime = 30
            }, 500)
        },
        searchUsers(query = '') {
            addSentryBreadcrumb('ui.action', 'Wyszukiwanie użytkownika')

            this.$store.dispatch('administration/findUsers', {
                paginate: '?limit=20',
                query: query
            })
                .then(response => {
                    this.users = response.data.results
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.usersSearchLoading = false
                })
        },
        userNameHandler(user) {
            if (user.firstName && user.lastName) {
                return `${user.firstName} ${user.lastName}`
            } else {
                return user.username
            }
        }
    }
}
</script>

